import { steamProfileUrl } from "@/lib/steamId";
import { getLang, translate } from "@/i18n/I18nProvider";
import { formatBRL, formatQty, simulatorNumberLocale } from "./vipSimulatorUtils";

export const VIP_DISCORD_TICKET_URL = "https://discord.gg/Xyyu55PRqX";

function sanitizeFilename(name, lang) {
    const fallback = translate("simulator.invoice.fileDefault", null, lang);
    return (name || fallback)
        .trim()
        .replace(/[^\w\s-áàâãéêíóôõúçÁÀÂÃÉÊÍÓÔÕÚÇ]/gi, "")
        .replace(/\s+/g, "-")
        .slice(0, 48) || fallback;
}

/** Data/hora no idioma da fatura. */
function formatDatePt(lang) {
    return new Date().toLocaleString(simulatorNumberLocale(lang), {
        dateStyle: "long",
        timeStyle: "short",
    });
}

function escapeHtml(str) {
    return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function buildItemRows(items, lang) {
    if (!items?.length) {
        return `<tr><td colspan="4" class="table-empty">${escapeHtml(translate("simulator.invoice.emptyItems", null, lang))}</td></tr>`;
    }
    return items
        .map(
            (item, i) => `
        <tr class="${i % 2 === 0 ? "row-even" : "row-odd"}">
          <td class="col-item">
            <span class="item-name">${escapeHtml(item.name)}</span>
          </td>
          <td class="num col-qty">×${formatQty(item.quantity, lang)}</td>
          <td class="num col-unit">${formatBRL(item.unitPrice, lang)}</td>
          <td class="num col-sub strong">${formatBRL(item.subtotal, lang)}</td>
        </tr>`,
        )
        .join("");
}

function buildPlayerIdentityHtml(player, lang) {
    if (!player) return "";
    const avatar = player.discordAvatarUrl
        ? `<img class="player-card__avatar" src="${escapeHtml(player.discordAvatarUrl)}" alt="" width="72" height="72" />`
        : `<div class="player-card__avatar player-card__avatar--placeholder" aria-hidden></div>`;
    const tag = escapeHtml(player.discordTag || player.discordUsername || "Discord");
    const displayName = escapeHtml(player.discordGlobalName || player.discordUsername || "—");
    const steam = escapeHtml(player.steamId || "—");
    const steamUrl = steamProfileUrl(player.steamId);
    const steamLink = steamUrl
        ? `<a class="player-card__steam-link" href="${escapeHtml(steamUrl)}" target="_blank" rel="noopener">${escapeHtml(translate("simulator.invoice.openSteam", null, lang))}</a>`
        : "";

    return `
        <section class="section section--player">
          <div class="section-head">
            <h2 class="section-title">${escapeHtml(translate("simulator.invoice.player", null, lang))}</h2>
          </div>
          <div class="player-card">
            ${avatar}
            <div class="player-card__body">
              <div class="player-card__name">${displayName}</div>
              <div class="player-card__tag">${tag}</div>
              <div class="player-card__steam-label">SteamID</div>
              <div class="player-card__steam">${steam}</div>
            </div>
          </div>
        </section>`;
}

function buildBenefitCards(benefits, lang) {
    if (!benefits?.length) {
        return `<p class="empty-state">${escapeHtml(translate("simulator.invoice.noBenefits", null, lang))}</p>`;
    }
    return `<div class="benefit-grid">${benefits
        .map(
            (b) => `
      <div class="benefit-chip">
        <span class="benefit-chip__dot" aria-hidden></span>
        <span class="benefit-chip__text">${escapeHtml(b.text)}</span>
      </div>`,
        )
        .join("")}</div>`;
}

/**
 * @param {{
 *   name: string;
 *   budget: number;
 *   benefits: { text: string }[];
 *   items: object[];
 *   total: number;
 *   kitTotal?: number;
 *   benefitsExtra?: number;
 *   player?: { discordAvatarUrl?: string | null; discordTag?: string; discordGlobalName?: string | null; discordUsername?: string | null; steamId?: string };
 * }} data
 * @param {"pt"|"en"} [lang] — idioma da fatura (por omissão o idioma atual do site)
 */
export function buildInvoiceHtml(data, lang = getLang()) {
    const { name, budget, benefits, items, total, kitTotal, benefitsExtra, player } = data;
    const kit = kitTotal ?? total;
    const extras = benefitsExtra ?? 0;
    const itemCount = items?.length ?? 0;
    const displayName = name?.trim() || "—";
    const generatedAt = formatDatePt(lang);
    /** Texto traduzido e escapado para HTML. */
    const tx = (key, vars) => escapeHtml(translate(key, vars, lang));

    return `<!DOCTYPE html>
<html lang="${tx("simulator.invoice.htmlLang")}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${tx("simulator.invoice.docTitle", { name: displayName })}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,600;0,9..40,700;1,9..40,400&family=Libre+Baskerville:wght@700&display=swap" rel="stylesheet" />
  <style>
    :root {
      --bg: #080605;
      --surface: #12100d;
      --surface-2: #1a1612;
      --border: rgba(255, 255, 255, 0.09);
      --gold: #ffb800;
      --gold-soft: #e8a656;
      --gold-dim: rgba(255, 184, 0, 0.14);
      --text: #f4efe4;
      --muted: rgba(244, 239, 228, 0.58);
      --discord: #5865f2;
    }
    * { box-sizing: border-box; margin: 0; }
    body {
      font-family: "DM Sans", "Segoe UI", system-ui, sans-serif;
      background: var(--bg);
      color: var(--text);
      padding: 40px 16px 48px;
      line-height: 1.55;
      -webkit-font-smoothing: antialiased;
    }
    .page {
      max-width: 800px;
      margin: 0 auto;
    }
    .ticket {
      border-radius: 20px;
      overflow: hidden;
      border: 1px solid rgba(255, 184, 0, 0.22);
      background: var(--surface);
      box-shadow:
        0 0 0 1px rgba(0, 0, 0, 0.4),
        0 32px 80px rgba(0, 0, 0, 0.55),
        0 0 120px rgba(255, 184, 0, 0.06);
    }

    /* —— Header —— */
    .ticket-header {
      position: relative;
      padding: 32px 36px 28px;
      background:
        linear-gradient(135deg, rgba(255, 184, 0, 0.18) 0%, transparent 55%),
        linear-gradient(180deg, #221a12 0%, #14110e 100%);
      border-bottom: 1px solid var(--border);
    }
    .ticket-header::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: 200px;
      height: 200px;
      background: radial-gradient(circle at 100% 0%, rgba(255, 184, 0, 0.15), transparent 70%);
      pointer-events: none;
    }
    .header-top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
      flex-wrap: wrap;
      position: relative;
      z-index: 1;
    }
    .brand-lockup {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .brand-tag {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: var(--gold-soft);
    }
    .brand-tag::before {
      content: "";
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--gold);
      box-shadow: 0 0 12px rgba(255, 184, 0, 0.7);
    }
    .ticket-title {
      font-family: "Libre Baskerville", Georgia, serif;
      font-size: clamp(1.5rem, 4vw, 1.85rem);
      font-weight: 700;
      color: var(--gold);
      line-height: 1.2;
      margin-top: 4px;
    }
    .ticket-subtitle {
      font-size: 14px;
      color: var(--muted);
      margin-top: 6px;
      max-width: 420px;
    }
    .badge-vip {
      flex-shrink: 0;
      padding: 10px 16px;
      border-radius: 999px;
      background: rgba(255, 184, 0, 0.12);
      border: 1px solid rgba(255, 184, 0, 0.35);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--gold);
      text-align: center;
      line-height: 1.35;
    }

    /* —— Body —— */
    .ticket-body {
      padding: 28px 36px 36px;
    }
    .section {
      margin-bottom: 28px;
    }
    .section:last-child {
      margin-bottom: 0;
    }
    .section-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 14px;
      padding-bottom: 10px;
      border-bottom: 1px solid var(--border);
    }
    .section-title {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--gold-soft);
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .section-title::before {
      content: "";
      width: 4px;
      height: 14px;
      border-radius: 2px;
      background: var(--gold);
    }
    .section-badge {
      font-size: 11px;
      font-weight: 600;
      color: var(--muted);
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid var(--border);
    }

    /* Info cards */
    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
    }
    .info-card {
      padding: 14px 16px;
      border-radius: 12px;
      background: var(--surface-2);
      border: 1px solid var(--border);
    }
    .info-card__label {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 6px;
    }
    .info-card__value {
      font-size: 16px;
      font-weight: 600;
      color: var(--text);
      word-break: break-word;
    }
    .info-card__value--gold {
      color: var(--gold);
      font-size: 18px;
    }

    .player-card {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 18px 20px;
      border-radius: 14px;
      border: 1px solid rgba(88, 101, 242, 0.35);
      background: linear-gradient(135deg, rgba(88, 101, 242, 0.12), rgba(0, 0, 0, 0.2));
    }
    .player-card__avatar {
      width: 72px;
      height: 72px;
      border-radius: 16px;
      object-fit: cover;
      flex-shrink: 0;
      border: 2px solid rgba(255, 255, 255, 0.12);
    }
    .player-card__avatar--placeholder {
      background: rgba(88, 101, 242, 0.25);
    }
    .player-card__body { min-width: 0; flex: 1; }
    .player-card__name {
      font-size: 18px;
      font-weight: 700;
      color: var(--text);
      line-height: 1.25;
    }
    .player-card__tag {
      margin-top: 4px;
      font-size: 14px;
      color: #aab0ff;
      font-weight: 600;
    }
    .player-card__steam-label {
      margin-top: 14px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: var(--muted);
    }
    .player-card__steam {
      margin-top: 4px;
      font-size: 15px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: var(--gold-soft);
      word-break: break-all;
    }
    .player-card__steam-link {
      display: inline-block;
      margin-top: 10px;
      font-size: 13px;
      font-weight: 600;
      color: #66c0f4;
      text-decoration: none;
    }
    .player-card__steam-link:hover { text-decoration: underline; }

    /* Table */
    .table-wrap {
      border-radius: 12px;
      border: 1px solid var(--border);
      overflow: hidden;
      background: var(--surface-2);
    }
    .table-scroll {
      overflow-x: auto;
      max-height: 420px;
      overflow-y: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    thead {
      position: sticky;
      top: 0;
      z-index: 2;
      background: #1f1a15;
    }
    th {
      padding: 12px 14px;
      text-align: left;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--muted);
      border-bottom: 1px solid var(--border);
    }
    td {
      padding: 11px 14px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.04);
      vertical-align: middle;
    }
    tr:last-child td { border-bottom: none; }
    .row-even { background: rgba(255, 255, 255, 0.02); }
    .row-odd { background: transparent; }
    .col-item { min-width: 200px; }
    .item-name { font-weight: 500; color: var(--text); }
    .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .col-sub { color: var(--gold-soft); }
    .strong { font-weight: 700; color: var(--gold); }
    .table-empty {
      text-align: center;
      color: var(--muted);
      padding: 24px !important;
    }

    /* Benefits */
    .benefit-grid {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .benefit-chip {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 14px;
      border-radius: 10px;
      background: var(--surface-2);
      border: 1px solid var(--border);
    }
    .benefit-chip__dot {
      flex-shrink: 0;
      width: 8px;
      height: 8px;
      margin-top: 6px;
      border-radius: 50%;
      background: var(--gold);
    }
    .benefit-chip__text {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.45;
    }
    .empty-state {
      font-size: 14px;
      color: var(--muted);
      font-style: italic;
      padding: 12px 0;
    }

    /* Totals */
    .totals-panel {
      border-radius: 14px;
      overflow: hidden;
      border: 1px solid rgba(255, 184, 0, 0.25);
      background: linear-gradient(165deg, rgba(255, 184, 0, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%);
    }
    .totals-rows {
      padding: 16px 20px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 16px;
      font-size: 14px;
    }
    .totals-row__label { color: var(--muted); }
    .totals-row__value {
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }
    .totals-grand {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      padding: 20px 22px;
      background: rgba(0, 0, 0, 0.35);
      border-top: 1px solid rgba(255, 184, 0, 0.2);
    }
    .totals-grand__label {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--muted);
    }
    .totals-grand__value {
      font-size: clamp(1.75rem, 5vw, 2.125rem);
      font-weight: 800;
      color: var(--gold);
      font-variant-numeric: tabular-nums;
      line-height: 1;
    }
    .note-negotiable {
      margin-top: 14px;
      padding: 12px 16px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px dashed rgba(255, 255, 255, 0.12);
      font-size: 13px;
      color: var(--muted);
      text-align: center;
    }

    /* Discord CTA */
    .cta-discord {
      margin-top: 28px;
      padding: 22px 24px;
      border-radius: 14px;
      background: linear-gradient(135deg, rgba(88, 101, 242, 0.18) 0%, rgba(88, 101, 242, 0.06) 100%);
      border: 1px solid rgba(88, 101, 242, 0.4);
    }
    .cta-discord__title {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #aab4ff;
      margin-bottom: 14px;
    }
    .cta-steps {
      list-style: none;
      padding: 0;
      margin: 0 0 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .cta-steps li {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      font-size: 14px;
      line-height: 1.45;
    }
    .cta-steps__num {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: var(--discord);
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .cta-link {
      display: inline-block;
      padding: 10px 18px;
      border-radius: 8px;
      background: var(--discord);
      color: #fff !important;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      word-break: break-all;
    }
    .cta-link:hover { filter: brightness(1.1); }

    .ticket-footer {
      padding: 16px 36px 20px;
      border-top: 1px solid var(--border);
      text-align: center;
      font-size: 11px;
      color: rgba(244, 239, 228, 0.35);
      letter-spacing: 0.06em;
    }

    @media (max-width: 560px) {
      .ticket-header, .ticket-body { padding-left: 20px; padding-right: 20px; }
      th, td { padding: 10px 10px; font-size: 12px; }
    }
    @media print {
      body { background: #fff; padding: 0; color: #1a1a1a; }
      .ticket {
        box-shadow: none;
        border-color: #ccc;
        max-width: 100%;
      }
      .ticket-header { background: #f5f0e8 !important; }
      .ticket-title, .totals-grand__value, .strong, .col-sub { color: #8a5a00 !important; }
      .cta-discord { border-color: #5865f2; }
    }
  </style>
</head>
<body>
  <div class="page">
    <article class="ticket">
      <header class="ticket-header">
        <div class="header-top">
          <div class="brand-lockup">
            <span class="brand-tag">ZRS — Zaralha Servers</span>
            <h1 class="ticket-title">${tx("simulator.invoice.title")}</h1>
            <p class="ticket-subtitle">${tx("simulator.invoice.subtitle")}</p>
          </div>
          <div class="badge-vip">VIP<br />${tx("simulator.invoice.badge")}</div>
        </div>
      </header>

      <main class="ticket-body">
        ${buildPlayerIdentityHtml(player, lang)}

        <section class="section">
          <div class="section-head">
            <h2 class="section-title">${tx("simulator.invoice.orderData")}</h2>
          </div>
          <div class="info-grid">
            <div class="info-card">
              <div class="info-card__label">${tx("simulator.invoice.vipName")}</div>
              <div class="info-card__value">${escapeHtml(displayName)}</div>
            </div>
            <div class="info-card">
              <div class="info-card__label">${tx("simulator.invoice.budget")}</div>
              <div class="info-card__value info-card__value--gold">R$ ${Number(budget).toLocaleString(simulatorNumberLocale(lang))}</div>
            </div>
            <div class="info-card">
              <div class="info-card__label">${tx("simulator.invoice.generatedAt")}</div>
              <div class="info-card__value">${escapeHtml(generatedAt)}</div>
            </div>
          </div>
        </section>

        <section class="section">
          <div class="section-head">
            <h2 class="section-title">${tx("simulator.invoice.kitItems")}</h2>
            <span class="section-badge">${tx("simulator.invoice.itemCount", { count: itemCount })}</span>
          </div>
          <div class="table-wrap">
            <div class="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>${tx("simulator.invoice.colItem")}</th>
                    <th class="num">${tx("simulator.invoice.colQty")}</th>
                    <th class="num">${tx("simulator.invoice.colUnit")}</th>
                    <th class="num">${tx("simulator.invoice.colSub")}</th>
                  </tr>
                </thead>
                <tbody>
                  ${buildItemRows(items, lang)}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="section">
          <div class="section-head">
            <h2 class="section-title">${tx("simulator.invoice.extraBenefits")}</h2>
            <span class="section-badge">${tx("simulator.invoice.selectedCount", { count: benefits?.length ?? 0 })}</span>
          </div>
          ${buildBenefitCards(benefits, lang)}
        </section>

        <section class="section">
          <div class="section-head">
            <h2 class="section-title">${tx("simulator.invoice.financial")}</h2>
          </div>
          <div class="totals-panel">
            <div class="totals-rows">
              <div class="totals-row">
                <span class="totals-row__label">${tx("simulator.invoice.kitSubtotal")}</span>
                <span class="totals-row__value">${formatBRL(kit, lang)}</span>
              </div>
              ${
                  extras > 0
                      ? `<div class="totals-row">
                <span class="totals-row__label">${tx("simulator.invoice.extras")}</span>
                <span class="totals-row__value">${formatBRL(extras, lang)}</span>
              </div>`
                      : ""
              }
            </div>
            <div class="totals-grand">
              <span class="totals-grand__label">${tx("simulator.invoice.total")}</span>
              <span class="totals-grand__value">${formatBRL(total, lang)}</span>
            </div>
          </div>
          <p class="note-negotiable">${tx("simulator.invoice.negotiable")}</p>
        </section>

        <aside class="cta-discord">
          <p class="cta-discord__title">${tx("simulator.invoice.nextStep")}</p>
          <ol class="cta-steps">
            <li>
              <span class="cta-steps__num">1</span>
              <span>${escapeHtml(translate("simulator.invoice.step1", null, lang)).replace("{vips}", "<strong>VIPs</strong>")}</span>
            </li>
            <li>
              <span class="cta-steps__num">2</span>
              <span>${tx("simulator.invoice.step2")}</span>
            </li>
            <li>
              <span class="cta-steps__num">3</span>
              <span>${tx("simulator.invoice.step3")}</span>
            </li>
          </ol>
          <a class="cta-link" href="${VIP_DISCORD_TICKET_URL}" target="_blank" rel="noopener">${VIP_DISCORD_TICKET_URL}</a>
        </aside>
      </main>

      <footer class="ticket-footer">
        ${tx("simulator.invoice.footer", { date: generatedAt })}
      </footer>
    </article>
  </div>
</body>
</html>`;
}

/**
 * Descarrega a fatura como ficheiro HTML (imprimível / anexável no Discord), no idioma atual.
 * @param {Parameters<typeof buildInvoiceHtml>[0]} data
 * @param {"pt"|"en"} [lang]
 */
export function downloadVipInvoice(data, lang = getLang()) {
    const html = buildInvoiceHtml(data, lang);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `${translate("simulator.invoice.filePrefix", null, lang)}-${sanitizeFilename(data.name, lang)}-${stamp}.html`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
